import { ReactElement, useCallback, useMemo } from 'react'
import { Label } from '@scene'
import { ColorRepresentation } from 'three'
import { useTheme } from '@mui/material'
import {
  DraggableLine,
  HighlightableLine,
  sceneConfig,
  ImmutableVector3,
} from '@modugen/ui-toolkit'
import { Line } from '@react-three/drei'
import { useControlStore, useModelStore, useSceneStore } from '@editorStores'
import { useElementLabel } from '@editorHooks'
import { useEnhanceClickEvent } from '../../hooks'

export enum PurlinElements {
  Line = 'PurlinLine',
  Handle = 'PurlinHandle',
}
interface Props {
  purlin: ShapeObjectLine
  isActive?: boolean
  onClick?: (beamGuid: string) => void
  isSecondary?: boolean
}

const Purlin2D = ({
  purlin,
  onClick,
  isActive = false,
  isSecondary = false,
}: Props): ReactElement => {
  const { scenePalette } = useTheme()
  const showLabels = useControlStore(state => state.showLabels)
  const purlinTypeLabelsVisible = useModelStore(
    state => state.labelVisibilityModel.visibilityByType.purlins,
  )
  const showPurlinLabels = showLabels && purlinTypeLabelsVisible
  const points = useMemo(
    () =>
      [purlin.shape.start, purlin.shape.end].map(({ x, y, z }) => new ImmutableVector3(x, y, z)),
    [purlin],
  )

  const getLabel = useElementLabel()
  const labelPosition = purlin.shape.start.add(purlin.shape.end).divideScalar(2)
  const labelOrientation =
    purlin.shape.start.x === purlin.shape.end.x ? 'vertical-lr' : 'horizontal-tb'

  // state picking due to performance reasons
  const mappedColor = useSceneStore(
    useCallback(state => state.guidToColor[purlin.guid], [purlin.guid]),
  ) as ColorRepresentation | undefined

  const { onClickEnhancedRef, onClickBaseRef, isClickable } = useEnhanceClickEvent(onClick)

  const label = (
    <Label
      label={getLabel(purlin.guid)}
      position={labelPosition.v}
      visible={showPurlinLabels}
      style={{
        backgroundColor: '#FFFFFF',
        color: scenePalette.elements2d.purlins.color as string,
        border: `1px solid ${scenePalette.elements2d.purlins.color as string}`,
        writingMode: labelOrientation,
      }}
    />
  )

  const backgroundElements = useMemo(() => {
    return (
      <>
        <group position={[0, 0, -0.001]} layers={sceneConfig.R3FNonSelectableObjectLayer}>
          <Line
            points={points.map(p => p.v)}
            color={'#FFFFFF'}
            lineWidth={scenePalette.elements2d.purlins.width}
            segments
            opacity={0.5}
          />
        </group>
      </>
    )
  }, [points, scenePalette.elements2d.purlins.width])

  if (isSecondary) {
    return (
      <>
        <HighlightableLine
          key={purlin.guid}
          lineName={purlin.guid}
          line={points}
          color={scenePalette.elements2d.purlins.color as string}
          width={scenePalette.elements2d.purlins.width}
          cursor="pointer"
          isHighlighted={false}
          // width={wallWidth}
          hoverable={false}
          clickable={false}
          onClick={() => onClickBaseRef.current(purlin.guid)}
          opacity={0.65}
          lineThreeProps={{
            segments: true,
            dashed: scenePalette.elements2d.purlins.dashed,
            dashSize: scenePalette.elements2d.purlins.dashSize,
            gap: scenePalette.elements2d.purlins.gap,
            dashScale: scenePalette.elements2d.purlins.dashScale,
            userData: {
              contextMenuActions: {
                hide: {
                  guid: purlin.guid,
                },
                properties: {
                  guid: purlin.guid,
                },
              },
            },
          }}
        />
        {backgroundElements}
        {label}
      </>
    )
  }

  return (
    <>
      <DraggableLine
        lineName={purlin.guid}
        line={{ start: points[0], end: points[1] }}
        color={scenePalette.elements2d.purlins.color as string}
        selectedColor={(mappedColor || scenePalette.elements2d.purlins.color) as string}
        isSelected={isActive}
        showLengthIndicator={false}
        onClick={() => onClickEnhancedRef.current(purlin.guid)}
        showIndicators={false}
        hoverable={isClickable}
        lineThreeProps={{
          lineWidth: isActive ? 4 : scenePalette.elements2d.purlins.width,
          segments: true,
          dashed: scenePalette.elements2d.purlins.dashed,
          dashSize: scenePalette.elements2d.purlins.dashSize,
          gap: scenePalette.elements2d.purlins.gap,
          dashScale: scenePalette.elements2d.purlins.dashScale,
          userData: {
            type: PurlinElements.Line,
            contextMenuActions: {
              hide: {
                guid: purlin.guid,
              },
              properties: {
                guid: purlin.guid,
              },
            },
          },
        }}
      />
      {backgroundElements}
      {label}
    </>
  )
}

export default Purlin2D
